/**
 * 🏙️ SEO CITY PAGE - Page de référencement local
 * Design épuré, copywriting premium
 * ZÉRO photo de personnes - Icônes et gradients uniquement
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Heart, MapPin, Users, Shield, Sparkles, Mic,
  ArrowRight, Menu, X
} from 'lucide-react';

// Données des villes
const CITY_DATA = {
  'paris': { name: 'Paris', region: 'Île-de-France', population: '2.1M', singles: '850 000', emoji: '🗼', description: 'la ville lumière', quartiers: ['Le Marais', 'Montmartre', 'Saint-Germain', 'Bastille', 'Oberkampf'], vibe: 'romantique' },
  'marseille': { name: 'Marseille', region: 'PACA', population: '870K', singles: '320 000', emoji: '⛵', description: 'la cité phocéenne', quartiers: ['Le Panier', 'Cours Julien', 'Vieux-Port', 'La Joliette', 'Endoume'], vibe: 'ensoleillée' },
  'lyon': { name: 'Lyon', region: 'Auvergne-Rhône-Alpes', population: '520K', singles: '195 000', emoji: '🦁', description: 'la capitale des Gaules', quartiers: ['Vieux Lyon', 'Croix-Rousse', 'Presqu\'île', 'Confluence', 'Guillotière'], vibe: 'gourmande' },
  'bordeaux': { name: 'Bordeaux', region: 'Nouvelle-Aquitaine', population: '260K', singles: '98 000', emoji: '🍷', description: 'la belle endormie', quartiers: ['Chartrons', 'Saint-Pierre', 'Saint-Michel', 'Bastide', 'Capucins'], vibe: 'élégante' },
  'lille': { name: 'Lille', region: 'Hauts-de-France', population: '235K', singles: '92 000', emoji: '🏛️', description: 'la capitale des Flandres', quartiers: ['Vieux-Lille', 'Wazemmes', 'Fives', 'Moulins', 'Euralille'], vibe: 'conviviale' },
  'toulouse': { name: 'Toulouse', region: 'Occitanie', population: '490K', singles: '185 000', emoji: '🌹', description: 'la ville rose', quartiers: ['Capitole', 'Saint-Cyprien', 'Carmes', 'Les Minimes', 'Compans'], vibe: 'festive' },
  'nice': { name: 'Nice', region: 'PACA', population: '340K', singles: '125 000', emoji: '🌴', description: 'la perle de la Riviera', quartiers: ['Vieux-Nice', 'Carré d\'Or', 'Cimiez', 'Port', 'Libération'], vibe: 'méditerranéenne' },
  'nantes': { name: 'Nantes', region: 'Pays de la Loire', population: '320K', singles: '118 000', emoji: '🐘', description: 'la cité des Ducs', quartiers: ['Bouffay', 'Graslin', 'Île de Nantes', 'Talensac', 'Chantenay'], vibe: 'créative' },
  'strasbourg': { name: 'Strasbourg', region: 'Grand Est', population: '285K', singles: '105 000', emoji: '🥨', description: 'la capitale européenne', quartiers: ['Petite France', 'Krutenau', 'Neustadt', 'Esplanade', 'Orangerie'], vibe: 'multiculturelle' },
  'montpellier': { name: 'Montpellier', region: 'Occitanie', population: '295K', singles: '115 000', emoji: '☀️', description: 'la surdouée', quartiers: ['Écusson', 'Antigone', 'Beaux-Arts', 'Boutonnet', 'Port Marianne'], vibe: 'jeune' },
  'rennes': { name: 'Rennes', region: 'Bretagne', population: '220K', singles: '88 000', emoji: '🦊', description: 'la capitale bretonne', quartiers: ['Centre historique', 'Sainte-Anne', 'Thabor', 'Villejean', 'Beaulieu'], vibe: 'étudiante' },
  'reims': { name: 'Reims', region: 'Grand Est', population: '185K', singles: '68 000', emoji: '🥂', description: 'la cité des sacres', quartiers: ['Centre-ville', 'Clairmarais', 'Wilson', 'Courlancy', 'Jean-Jaurès'], vibe: 'raffinée' },
  'le-havre': { name: 'Le Havre', region: 'Normandie', population: '170K', singles: '62 000', emoji: '🚢', description: 'la porte océane', quartiers: ['Centre reconstruit', 'Sainte-Adresse', 'Danton', 'Graville', 'Sanvic'], vibe: 'maritime' },
  'saint-etienne': { name: 'Saint-Étienne', region: 'Auvergne-Rhône-Alpes', population: '175K', singles: '65 000', emoji: '⚙️', description: 'la ville du design', quartiers: ['Centre-ville', 'Châteaucreux', 'Bellevue', 'Montreynaud', 'La Terrasse'], vibe: 'créative' },
  'toulon': { name: 'Toulon', region: 'PACA', population: '180K', singles: '66 000', emoji: '⚓', description: 'la cité de la Marine', quartiers: ['Vieille ville', 'Le Mourillon', 'La Serinette', 'Pont-du-Las', 'Cap Brun'], vibe: 'authentique' },
  'grenoble': { name: 'Grenoble', region: 'Auvergne-Rhône-Alpes', population: '160K', singles: '62 000', emoji: '🏔️', description: 'la capitale des Alpes', quartiers: ['Hyper-centre', 'Saint-Bruno', 'Championnet', 'Île Verte', 'Europole'], vibe: 'sportive' },
  'dijon': { name: 'Dijon', region: 'Bourgogne-Franche-Comté', population: '160K', singles: '58 000', emoji: '🍇', description: 'la cité des Ducs', quartiers: ['Centre historique', 'Toison d\'Or', 'Port du Canal', 'Fontaine d\'Ouche', 'Les Grésilles'], vibe: 'gourmande' },
  'angers': { name: 'Angers', region: 'Pays de la Loire', population: '155K', singles: '58 000', emoji: '🏰', description: 'la douceur angevine', quartiers: ['Centre-ville', 'La Doutre', 'Saint-Serge', 'Belle-Beille', 'Monplaisir'], vibe: 'douce' },
  'nimes': { name: 'Nîmes', region: 'Occitanie', population: '150K', singles: '55 000', emoji: '🏛️', description: 'la Rome française', quartiers: ['Écusson', 'Gambetta', 'Jean-Jaurès', 'Costières', 'Mas de Mingue'], vibe: 'historique' },
  'villeurbanne': { name: 'Villeurbanne', region: 'Auvergne-Rhône-Alpes', population: '150K', singles: '58 000', emoji: '🎭', description: 'la voisine de Lyon', quartiers: ['Gratte-Ciel', 'Charpennes', 'République', 'Cusset', 'Tonkin'], vibe: 'culturelle' }
};

const SeoCityPage = ({ citySlug }) => {
  const navigate = useNavigate();
  const city = CITY_DATA[citySlug];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [onlineCount, setOnlineCount] = useState(Math.floor(Math.random() * 50) + 30);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (!city) navigate('/');
  }, [city, navigate]);

  // SEO Meta tags
  useEffect(() => {
    if (city) {
      document.title = `Rencontre à ${city.name} | Heartbeats - L'amour mérite mieux`;
      
      const updateMeta = (selector, attr, value) => {
        let el = document.querySelector(selector);
        if (!el) {
          el = document.createElement('meta');
          if (selector.includes('property')) el.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
          else el.name = selector.match(/name="([^"]+)"/)[1];
          document.head.appendChild(el);
        }
        el.setAttribute(attr, value);
      };

      updateMeta('meta[name="description"]', 'content', `${city.singles} célibataires à ${city.name}. Pas de swipe, des vraies conversations. Rejoins Heartbeats gratuitement.`);
      updateMeta('meta[property="og:title"]', 'content', `Rencontre à ${city.name} ${city.emoji} | Heartbeats`);
      updateMeta('meta[property="og:description"]', 'content', `L'amour mérite mieux qu'un swipe. ${city.singles} célibataires t'attendent à ${city.name}.`);
      
      // 🔗 CANONICAL URL - Évite le contenu dupliqué Google
      let canonical = document.getElementById('canonical-link');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.id = 'canonical-link';
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = `https://heartbeats.fr/rencontre-${citySlug}`;
    }
  }, [city, citySlug]);

  useEffect(() => {
    const interval = setInterval(() => {
      setOnlineCount(prev => Math.max(20, prev + Math.floor(Math.random() * 10) - 5));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  if (!city) return null;

  const features = [
    { icon: Heart, gradient: 'from-[#dc2626] to-[#ec4899]', title: "Pas de swipe", desc: "Des vraies conversations, pas du jetable" },
    { icon: Mic, gradient: 'from-[#8b5cf6] to-[#6366f1]', title: "Salons vocaux", desc: "Parle avant de matcher" },
    { icon: MapPin, gradient: 'from-[#22d3ee] to-[#0ea5e9]', title: "Près de toi", desc: `Célibataires à ${city.name}` },
    { icon: Shield, gradient: 'from-[#10b981] to-[#059669]', title: "100% vérifié", desc: "Zéro fake, zéro bot" }
  ];

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0a0015] text-white" style={{ overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }} data-testid={`seo-city-${citySlug}`}>
      
      {/* Fond */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#dc2626]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[150px]" />
      </div>

      {/* Cœurs subtils */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {[...Array(8)].map((_, i) => (
          <motion.div key={`heart-float-${i}`} className="absolute text-[#dc2626]/15"
            style={{ left: `${10 + (i * 12) % 80}%`, bottom: '-30px', fontSize: `${10 + (i % 3) * 3}px` }}
            animate={{ y: [0, -1500], rotate: [0, 180], opacity: [0.15, 0.3, 0.15, 0] }}
            transition={{ duration: 25 + (i % 5) * 5, repeat: Infinity, delay: i * 2, ease: 'linear' }}>
            <Heart fill="currentColor" />
          </motion.div>
        ))}
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#0a0015]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Heart className="w-7 h-7 text-[#dc2626]" fill="currentColor" />
              <span className="text-xl font-bold"><span className="text-white">Heart</span><span className="text-[#dc2626]">beats</span></span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('quartiers')} className="text-gray-400 hover:text-white text-sm">Quartiers</button>
              <button onClick={() => scrollToSection('features')} className="text-gray-400 hover:text-white text-sm">Avantages</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-400 hover:text-white text-sm">FAQ</button>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/auth?mode=login" className="text-gray-400 hover:text-white text-sm">Connexion</Link>
              <Link to="/auth?mode=signup" className="px-5 py-2.5 bg-[#dc2626] hover:bg-[#b91c1c] rounded-lg font-medium text-sm">S'inscrire</Link>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-[#0a0015] border-t border-white/5 py-4 px-4">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('quartiers')} className="text-left text-gray-400 py-2">Quartiers</button>
              <button onClick={() => scrollToSection('features')} className="text-left text-gray-400 py-2">Avantages</button>
              <hr className="border-white/10" />
              <Link to="/auth?mode=login" className="text-gray-400 py-2">Connexion</Link>
              <Link to="/auth?mode=signup" className="bg-[#dc2626] py-3 rounded-xl font-medium text-center">S'inscrire</Link>
            </div>
          </motion.div>
        )}
      </nav>

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            
            {/* Badge ville */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-gray-400 mb-8">
              <MapPin className="w-4 h-4 text-[#dc2626]" />
              {city.name}, {city.region}
              <span className="text-lg">{city.emoji}</span>
            </div>

            {/* Titre */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] mb-6">
              Rencontre à <span className="text-[#dc2626]">{city.name}.</span><br />
              <span className="text-gray-400 text-3xl sm:text-4xl font-bold">Pour de vrai.</span>
            </h1>

            {/* Sous-titre */}
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              {city.singles} célibataires dans {city.description}.<br />
              Pas de swipe infini. Des vraies conversations.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500 mb-10">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                {onlineCount} en ligne à {city.name}
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                100% profils vérifiés
              </div>
            </div>

            {/* CTA Principal */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Link to="/auth?mode=signup"
                className="px-8 py-4 bg-[#dc2626] hover:bg-[#b91c1c] rounded-xl font-bold text-lg transition-all flex items-center gap-3 shadow-lg shadow-[#dc2626]/30">
                <Heart className="w-5 h-5" fill="currentColor" />
                Créer mon compte gratuit
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/auth?mode=login"
                className="px-6 py-4 bg-white/10 hover:bg-white/15 rounded-xl font-medium transition-all border border-white/20">
                J'ai déjà un compte
              </Link>
            </div>

            <p className="text-gray-600 text-sm">Inscription gratuite • Sans engagement</p>
          </motion.div>
        </div>
      </section>

      {/* QUARTIERS */}
      <section id="quartiers" className="py-16 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">
            Célibataires dans les quartiers de <span className="text-[#dc2626]">{city.name}</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {city.quartiers.map((quartier, idx) => (
              <motion.div key={quartier} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}
                className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 hover:border-[#dc2626]/50 transition-colors text-gray-300 text-sm">
                {quartier}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 PILIERS */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-12">
            Pourquoi <span className="text-[#dc2626]">Heartbeats</span> ?
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-6">
            {features.map((f, idx) => (
              <motion.div key={`feature-${f.title}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}
                className="p-6 rounded-2xl bg-white/[0.03] border border-white/10">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                  <f.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA MILIEU */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center p-8 rounded-3xl bg-gradient-to-br from-[#dc2626]/10 to-purple-600/10 border border-white/10">
          <Sparkles className="w-12 h-12 text-[#dc2626] mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-2">Prêt à tenter l'aventure ?</h3>
          <p className="text-gray-400 mb-6">Rejoins des milliers de célibataires à {city.name}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth?mode=signup"
              className="px-8 py-4 bg-[#dc2626] hover:bg-[#b91c1c] rounded-xl font-bold transition-all flex items-center gap-2">
              <Heart className="w-5 h-5" fill="currentColor" />
              Créer mon compte
            </Link>
            <Link to="/auth?mode=login"
              className="px-6 py-3 text-gray-400 hover:text-white transition-colors">
              Déjà inscrit ? Se connecter
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Questions fréquentes</h2>
          
          {[
            { q: `Heartbeats est-il gratuit à ${city.name} ?`, a: `Oui ! L'inscription et les fonctionnalités de base sont gratuites. Tu peux créer ton profil, voir les célibataires de ${city.name} et démarrer des conversations.` },
            { q: `Combien de célibataires à ${city.name} ?`, a: `${city.name} compte environ ${city.singles} célibataires, et notre communauté grandit chaque jour.` },
            { q: `C'est quoi la différence avec Tinder ?`, a: `Pas de swipe. Ici tu prends le temps de découvrir les gens avec des conversations et des salons vocaux. C'est plus humain, plus respectueux.` }
          ].map((faq, idx) => (
            <motion.div key={`faq-${idx}-${city.name}`} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="mb-4 p-6 rounded-2xl bg-white/[0.03] border border-white/10">
              <h3 className="font-bold mb-2 text-[#dc2626]">{faq.q}</h3>
              <p className="text-gray-400 text-sm">{faq.a}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#dc2626] to-[#ec4899] flex items-center justify-center mx-auto mb-6">
            <Heart className="w-8 h-8 text-white" fill="currentColor" />
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-black mb-4">
            L'amour t'attend à<br /><span className="text-[#dc2626]">{city.name}.</span>
          </h2>
          
          <p className="text-gray-400 mb-8">
            Rejoins {city.singles} célibataires qui cherchent l'amour autrement.
          </p>

          <div className="max-w-md mx-auto mb-6">
            <CityEmailCaptureForm source="footer" citySlug={citySlug} />
          </div>

          <p className="text-gray-600 text-sm">Gratuit • Sans engagement</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#dc2626]" fill="currentColor" />
            <span className="font-bold">Heartbeats</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/mentions-legales?from=heartbeats" className="hover:text-white">Mentions légales</Link>
            <Link to="/cgu?from=heartbeats" className="hover:text-white">CGU</Link>
            <Link to="/confidentialite?from=heartbeats" className="hover:text-white">Confidentialité</Link>
          </div>
          <p className="text-sm text-gray-500">© 2026 Heartbeats - {city.name}</p>
        </div>
      </footer>
    </div>
  );
};

export default SeoCityPage;
